import { AbstractControl, FormGroup } from '@angular/forms';
import CodiceFiscale from 'codice-fiscale-js';
import { isBefore } from 'date-fns';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { getDatiIdentificativiControl, getDatiIdentificativiGroup } from './helpers/dati-identificativi-form-validators.helper';

export const datiIdentificativiCodiciRappresentanteRules: FormGroupRules = {
    my_CodFisc: (form: FormGroup) => {
        const my_CodFiscControl = getDatiIdentificativiControl(form, 'my_CodFisc');
        const errors = my_CodFiscControl.errors ? my_CodFiscControl.errors : {};
        const value = my_CodFiscControl?.value ?? '';
        const checkValues = value.includes('00000000000');
        const checkLength = my_CodFiscControl?.value?.length != 11;

        addRemoveError(checkValues, errors, 'my_CodFisc', {
            status: 'Attenzione: valore non valido',
        });

        addRemoveError(checkLength, errors, 'my_CodFisc', {
            status: 'Attenzione: codice fiscale non conforme',
            message: 'La lunghezza del codice fiscale non è corretta. Il codice fiscale deve essere lungo 11 caratteri',
        });
        if (!checkLength && !checkValues) {
            // Funzione presa da http://www.icosaedro.it/cf-pi/
            let somma = 0;
            for (let index = 0; index < 11; index++) {
                let somma1 = my_CodFiscControl.value.charCodeAt(index) - '0'.charCodeAt(0);
                if ((index & 1) === 1) {
                    somma1 = somma1 * 2;
                    if (somma1 > 9) {
                        somma1 = somma1 - 9;
                    }
                }
                somma = somma + somma1;
            }

            addRemoveError(somma % 10 !== 0, errors, 'my_CodFisc', {
                status: 'Attenzione: codice fiscale non valido',
            });
        }

        addRemoveError(isNilOrEmpty(my_CodFiscControl.value), errors, 'my_CodFisc', {
            status: REQUIRED_STATUS,
        });

        // VBS (OnLoad 1106-1117): se il CF contiene la matricola come sottostringa, è un dato
        // fittizio/placeholder e va azzerato in automatico. In NCR (sessione persistente, non
        // caricamento one-shot) si segnala invece con un errore bloccante, coerente col pattern
        // già adottato altrove nel progetto per gli auto-set/auto-correct del VBS (vedi REM-23).
        const my_Matricola: string = getDatiIdentificativiControl(form, 'my_Matricola')?.value;
        const containsMatricola = !isNilOrEmpty(my_Matricola) && value.includes(my_Matricola);
        addRemoveError(containsMatricola, errors, 'my_CodFisc', {
            status: 'Attenzione: valore non valido',
            message: 'Il codice fiscale non può contenere la matricola come sottostringa',
        });

        my_CodFiscControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_PartitaIVA: (form: FormGroup) => {
        const my_PartitaIVAControl = getDatiIdentificativiControl(form, 'my_PartitaIVA');
        const errors = my_PartitaIVAControl.errors ? my_PartitaIVAControl.errors : {};
        const value = my_PartitaIVAControl?.value ?? '';
        const checkValues = value.includes('00000000000');
        const checkLength = my_PartitaIVAControl?.value?.length != 11;

        addRemoveError(checkValues, errors, 'my_PartitaIVA', {
            status: 'La partita IVA non è valida',
        });

        addRemoveError(checkLength, errors, 'my_PartitaIVA', {
            status: 'Attenzione: partita IVA non conforme',
            message: 'La lunghezza della partita IVA non è corretta. La partita IVA deve essere lunga 11 caratteri',
        });

        if (!checkLength && !checkValues) {
            // Funzione presa da http://www.icosaedro.it/cf-pi/
            let somma = 0;
            for (let index = 0; index < 11; index++) {
                let somma1 = my_PartitaIVAControl.value.charCodeAt(index) - '0'.charCodeAt(0);
                if ((index & 1) === 1) {
                    somma1 = somma1 * 2;
                    if (somma1 > 9) {
                        somma1 = somma1 - 9;
                    }
                }
                somma = somma + somma1;
            }

            addRemoveError(somma % 10 !== 0, errors, 'my_PartitaIVA', {
                status: 'Attenzione: partita IVA errata',
            });
        }

        addRemoveError(isNilOrEmpty(my_PartitaIVAControl.value), errors, 'my_PartitaIVA', {
            status: REQUIRED_STATUS,
        });
        my_PartitaIVAControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataUltModStat: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_DataUltModStatControl = getControl(datiIdentificativiGroup, 'my_DataUltModStat');
        const my_CkModStatMaiModificata: string = getControl(datiIdentificativiGroup, 'my_CkModStatMaiModificata')?.value;
        const my_DataUltModStatOrig: string = getControl(datiIdentificativiGroup, 'my_DataUltModStatOrig')?.value;
        const errors = my_DataUltModStatControl.errors ? my_DataUltModStatControl.errors : {};
        const dateUltModStat = new Date(my_DataUltModStatControl.value);
        const dateUltModStatOrig = new Date(my_DataUltModStatOrig);
        const resultBefore = isBefore(dateUltModStat, dateUltModStatOrig);
        const resultEqual = isEqual(dateUltModStat, dateUltModStatOrig);

        addRemoveError((resultBefore || resultEqual) && my_CkModStatMaiModificata === '1' && !isNilOrEmpty(my_DataUltModStatOrig), errors, 'my_DataUltModStat', {
            status: 'Attenzione: verificare la data',
        });

        addRemoveError(isNilOrEmpty(my_DataUltModStatControl.value) && my_CkModStatMaiModificata === '1', errors, 'my_DataUltModStat', {
            status: REQUIRED_STATUS,
        });

        my_DataUltModStatControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_NIscrizRevis: (form: FormGroup) => {
        const my_NIscrizRevisControl = getDatiIdentificativiControl(form, 'my_NIscrizRevis');
        const checkValue: boolean = my_NIscrizRevisControl.value.startsWith('/') || my_NIscrizRevisControl.value.startsWith('0/') || my_NIscrizRevisControl.value.startsWith('0/0');
        const errors = my_NIscrizRevisControl.errors ? my_NIscrizRevisControl.errors : {};

        addRemoveError(isNilOrEmpty(my_NIscrizRevisControl?.value), errors, 'my_NIscrizRevis', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(checkValue, errors, 'my_NIscrizRevis', {
            status: 'Attenzione: campo non caricato in automatico',
            message: 'Se il campo non è caricato in automatico, comunicare al Servizio Nazionale tale numero.',
        });

        addRemoveError(
            !isNilOrEmpty(my_NIscrizRevisControl?.value) && (my_NIscrizRevisControl.value.startsWith('0/') || my_NIscrizRevisControl.value === '0/0'),
            errors,
            'my_NIscrizRevis_zero',
            { status: 'Attenzione: verificare il numero di iscrizione' }
        );

        my_NIscrizRevisControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_RappresentanteCoopCodFisc: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const nomeUtente: string = getControl(datiIdentificativiGroup, 'my_RappresentanteCoopNome')?.value;
        const cognomeUtente: string = getControl(datiIdentificativiGroup, 'my_RappresentanteCoopCognome')?.value;
        const codiceFiscaleControl = getControl(datiIdentificativiGroup, 'my_RappresentanteCoopCodFisc');
        validaCodiceFiscale(nomeUtente, cognomeUtente, codiceFiscaleControl);
    },
};

const validaCodiceFiscale = (nomeUtente: string, cognomeUtente: string, codiceFiscaleControl: AbstractControl) => {
    let error = codiceFiscaleControl.errors ? codiceFiscaleControl.errors : {};

    if (nomeUtente && cognomeUtente && codiceFiscaleControl?.value) {
        const codFiscContr = codiceFiscaleControl.value;
        const codFiscControlValid = CodiceFiscale.check(codFiscContr);
        const consonantiNome = nomeUtente.replace(/[^BCDFGHJKLMNPQRSTVWXYZ]/gi, '');
        const consonantiCognome = cognomeUtente.replace(/[^BCDFGHJKLMNPQRSTVWXYZ]/gi, '');
        const vocaliNome = nomeUtente.replace(/[^AEIOU]/gi, '');
        const vocaliCognome = cognomeUtente.replace(/[^AEIOU]/gi, '');
        const generatedCognome = (consonantiCognome + vocaliCognome + 'XXX').substr(0, 3);
        let generatedNome = consonantiNome;

        if (generatedNome.length >= 4) {
            generatedNome = consonantiNome.charAt(0) + consonantiNome.charAt(2) + consonantiNome.charAt(3);
        } else {
            generatedNome += vocaliNome;
            generatedNome += 'XXX';
            generatedNome = generatedNome.substr(0, 3);
        }

        const nomeCognome = (generatedCognome + generatedNome).toUpperCase();
        const codFisc = codiceFiscaleControl.value.substr(0, 6).toUpperCase();

        addRemoveError(codFiscControlValid === false, error, 'errore', {
            status: 'Attenzione: codice fiscale non conforme',
            message: "Errore sull'ultimo carattere del codice fiscale",
        });

        addRemoveError(nomeCognome != codFisc, error, 'errore', {
            status: 'Attenzione: codice fiscale non conforme',
            message: 'Il nome ed il cognome non corrispondono ai valori inseriti nel codice fiscale',
        });

        codiceFiscaleControl.setErrors(isEqual(error, {}) ? null : error);
    }
};
